import React, { useState } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { api } from './axiosInstance';
import SmsDigitInput from './SmsDigitInput';

const SmsEnrollmentFlow = ({ onBack, onComplete }) => {
    const [step, setStep] = useState('phone'); // 'phone' | 'code' | 'recovery'
    const [phoneNumber, setPhoneNumber] = useState('');
    const [e164Phone, setE164Phone] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState([]);

    const handleSendCode = async (e) => {
        e.preventDefault();
        setError('');

        let parsed;
        try {
            parsed = parsePhoneNumber(phoneNumber, 'US');
        } catch {
            setError('Please enter a valid phone number.');
            return;
        }

        if (!parsed || !parsed.isValid()) {
            setError('Please enter a valid phone number.');
            return;
        }

        const e164 = parsed.format('E.164');
        setLoading(true);

        try {
            await api.post('/mfa/sms/send-setup', { phoneNumber: e164 });
            setE164Phone(e164);
            setStep('code');
        } catch (err) {
            const status = err.response?.status;
            if (status === 409) {
                setError('Phone number already in use.');
            } else if (status === 429) {
                setError('Too many attempts. Please wait a moment and try again.');
            } else {
                setError('Could not send SMS. Check your number and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (code) => {
        setError('');
        setLoading(true);

        try {
            const response = await api.post('/mfa/sms/verify-setup', { phoneNumber: e164Phone, code });
            setRecoveryCodes(response.data.recoveryCodes || []);
            setStep('recovery');
        } catch (err) {
            const status = err.response?.status;
            const message = err.response?.data?.message || err.response?.data?.error || '';
            if (message.toLowerCase().includes('expired')) {
                setError('Code expired. Request a new code.');
            } else if (status === 429) {
                setError('Too many attempts. Please wait a moment and try again.');
            } else {
                setError('Invalid code. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (step === 'phone') {
        return (
            <div className="text-center">
                <button
                    onClick={onBack}
                    className="text-lightblue text-sm mb-4 flex items-center gap-1 hover:text-orange"
                    type="button"
                >
                    &larr; Back
                </button>
                <h2 className="text-xl font-bold text-orange mb-4">Setup SMS MFA</h2>
                <form onSubmit={handleSendCode}>
                    <label className="block text-lightgray text-sm mb-1 text-left">Phone Number</label>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="bg-deepblue text-lightgray border border-lightblue rounded p-2 text-sm">+1</span>
                        <input
                            type="tel"
                            placeholder="+1 (555) 000-0000"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            className="bg-deepblue text-lightgray border border-lightblue rounded p-2 w-full"
                            disabled={loading}
                        />
                    </div>
                    {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2 bg-orange text-white font-bold rounded hover:bg-deeporange transition disabled:opacity-50"
                    >
                        {loading ? 'Sending...' : 'Send Code'}
                    </button>
                </form>
            </div>
        );
    }

    if (step === 'code') {
        return (
            <div className="text-center">
                <button
                    onClick={() => setStep('phone')}
                    className="text-lightblue text-sm mb-4 flex items-center gap-1 hover:text-orange"
                    type="button"
                >
                    &larr; Back
                </button>
                <h2 className="text-xl font-bold text-orange mb-4">Enter Verification Code</h2>
                <p className="text-lightgray text-sm mb-4">Enter the 6-digit code sent to your phone</p>
                <SmsDigitInput
                    onComplete={handleVerifyCode}
                    disabled={loading}
                    error={!!error}
                />
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                {loading && (
                    <p className="text-lightgray text-sm mt-3">Verifying...</p>
                )}
            </div>
        );
    }

    // step === 'recovery'
    return (
        <div className="bg-bluegray p-4 rounded-lg text-center">
            <p className="text-green-400 font-bold mb-2">MFA Enabled!</p>
            <p className="text-red-400 font-bold text-sm mb-2">
                Save these backup codes. If you lose access to your phone, these are the only way to recover your account.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4">
                {recoveryCodes.map((code, index) => (
                    <div key={index} className="bg-deepblue p-2 text-white font-mono text-center rounded border border-lightblue">
                        {code}
                    </div>
                ))}
            </div>
            <button
                onClick={() => { if (onComplete) onComplete(); }}
                className="w-full py-2 bg-orange text-white font-bold rounded hover:bg-deeporange transition"
            >
                I have saved them
            </button>
        </div>
    );
};

export default SmsEnrollmentFlow;
